import type { History, Location } from "history";

/**
 * True when the current location was reached as a "deep link" — the browser's initial
 * entry (a bookmark, a pasted/shared URL, a new tab) rather than an in-app navigation.
 *
 * Heuristic: history v4 leaves `location.key` undefined on the initial browser entry
 * (window.history.state is null, and `if (key)` in createLocation skips assignment), and
 * `history.action` stays "POP" until any in-app push/replace. `history.length` is unreliable
 * because it reflects the browser tab stack, which is >= 2 on any direct URL paste into an
 * existing tab. Callers use this to avoid `goBack()` when there is no in-app predecessor
 * (which would walk off the SPA), preferring a `history.replace` to a known route instead.
 *
 * TODO(history-migration): This couples to history@4 / react-router@5 internals. If the
 * project upgrades to history@5 or migrates to react-router@6, this predicate likely changes
 * (history@5 always assigns a key; react-router@6 exposes a different navigation API). Search
 * "history-migration" before/after any history or react-router upgrade.
 *
 * `history.action`/`location.key` are read defensively so callers can pass partial objects
 * (e.g. a component whose `history`/`location` props aren't wired in a test).
 */
export function isDeepLinkEntry(
    history: Pick<History, "action"> | undefined,
    location: Pick<Location, "key"> | undefined,
): boolean {
    return history?.action === "POP" && !location?.key;
}
